import React from 'react'

import {
  AutocompleteInput,
  BooleanInput,
  CreateButton,
  Datagrid,
  FunctionField,
  List,
  ListProps,
  RaRecord,
  ReferenceField,
  ReferenceInput,
  SearchInput,
  ShowButton,
  TextField,
  TopToolbar,
  Translate,
  useTranslate,
} from 'react-admin'

import { Tooltip } from '@mantine/core'
import { API } from '../../helpers/Api.helper'
import { DateHelper } from '../../helpers/Date.helper'
import { ListHelper } from '../../helpers/List.helper'
import { useUserStore } from '../../stores/useUserStore'
import { Forbidden } from '../Forbidden'

type ArtworkListActionsProps = {
  isAuthorised: boolean
  translate: Translate
}
const ArtworkListActions = ({ isAuthorised, translate }: ArtworkListActionsProps): JSX.Element => (
  <TopToolbar>
    <Tooltip
      label={`⚠ ${translate('pages.users.lackingPermission')} → ${translate(
        'pages.users.permissionsList.artworks.edit',
      )}`}
      hidden={isAuthorised}
    >
      <span>
        <CreateButton disabled={!isAuthorised} />
      </span>
    </Tooltip>
  </TopToolbar>
)

export const ArtworksList = (props: ListProps): JSX.Element => {
  const translate = useTranslate()
  const userStore = useUserStore()

  if (!userStore.isAuthorised('artworks.read')) {
    return <Forbidden permission="artworks.read" />
  }

  return (
    <List
      {...props}
      actions={
        <ArtworkListActions
          isAuthorised={userStore.isAuthorised('artworks.edit')}
          translate={translate}
        />
      }
      exporter={false}
      perPage={API.DEFAULT_PAGINATION.perPage}
      sort={{ field: 'createdAt', order: 'DESC' }}
      filterDefaultValues={{ withoutEmptyAssets: true }}
      filters={[
        <SearchInput key="search" source="q" alwaysOn />,
        <SearchInput
          key="originalArtworkCode"
          source="originalArtworkCode"
          alwaysOn
          placeholder={translate('pages.artworks.artworkCode')}
        />,
        <ReferenceInput
          key="product.beneficiaryId"
          source="product.beneficiaryId"
          reference="beneficiaries"
          alwaysOn
          allowEmpty={false}
          sort={ListHelper.DEFAULT_SORT}
        >
          <AutocompleteInput optionText="name" label={translate('_generics.beneficiary')} />
        </ReferenceInput>,
        <ReferenceInput
          key="productId"
          source="productId"
          reference="products"
          alwaysOn
          allowEmpty={false}
          sort={ListHelper.DEFAULT_SORT}
        >
          <AutocompleteInput optionText="name" label={translate('_generics.product')} />
        </ReferenceInput>,
        <ReferenceInput
          source="youtubeAssetChannelId"
          reference="youtubeChannels"
          key="filterYT"
          alwaysOn
          allowEmpty={false}
          sort={ListHelper.DEFAULT_SORT}
        >
          <AutocompleteInput optionText="name" label={translate('_generics.youtubeChannel')} />
        </ReferenceInput>,
        <ReferenceInput
          source="youtubeAsset.id"
          reference="youtubeAssets"
          key="filterYTAsset"
          alwaysOn
          allowEmpty={false}
          sort={ListHelper.DEFAULT_SORT}
        >
          <AutocompleteInput optionText="id" label={translate('_generics.assetId')} />
        </ReferenceInput>,
        <BooleanInput
          key="withoutEmptyAssets"
          source="withoutEmptyAssets"
          label={translate('pages.artworks.withoutEmptyAssets')}
          alwaysOn
        />,
      ]}
    >
      <Datagrid rowClick="show" bulkActionButtons={false}>
        <TextField source="name" label={translate('_generics.name')} />
        <TextField source="originalArtworkCode" label={translate('pages.artworks.artworkCode')} />
        <ReferenceField
          source="productId"
          reference="products"
          label={translate('_generics.product')}
          link="show"
        >
          <TextField source="name" />
        </ReferenceField>
        <ReferenceField source="youtubeAssetChannelId" reference="youtubeChannels" link="show">
          <TextField source="name" />
        </ReferenceField>
        <FunctionField
          source="youtubeAssets"
          label={translate('_generics.youtubeAssets')}
          sortable={false}
          render={(record: RaRecord | undefined) => <span>{record?.youtubeAssets.length}</span>}
        />
        <FunctionField
          source="createdAt"
          label={translate('_generics.createdAt')}
          render={(record: RaRecord | undefined) => (
            <span>{DateHelper.format(record?.createdAt)}</span>
          )}
        />
        <ShowButton />
      </Datagrid>
    </List>
  )
}
